import { GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { type RefSelectProps, Divider, Select, type SelectProps } from 'antd'
import { forwardRef, type ReactNode, useCallback, useState } from 'react'
import type { BaseOptionType, DefaultOptionType } from 'antd/es/select'

/** Essentially, we want 3 parts:
 * 1. the cell with it's clickable button (set `supressClickEdit=true`, custom button that calls api.startEditingCell())
 *      this will also include the "editing" visual state
 * 2. the custom treeview/select component
 * 3. a wrapper that renders it as a popup, wraps it with `ag-custom-component-popup` class, and provides the data
 */

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
export type MappingDropdownProps<ValueType = any, OptionType extends BaseOptionType = DefaultOptionType> = SelectProps<
  ValueType,
  OptionType
> & {
  title?: ReactNode
  subtitle?: string
  inputHeader?: ReactNode
}

export const MappingDropdown = forwardRef<RefSelectProps, MappingDropdownProps>(
  ({ title, inputHeader, value, onChange, subtitle, ...rest }, ref) => {
    const [savedValue, setSavedValue] = useState<DefaultOptionType>()
    const onSave = useCallback(() => {
      onChange?.(savedValue?.value, savedValue)
      setSavedValue(undefined)
    }, [savedValue, onChange])
    return (
      <Vertical>
        {title}
        {subtitle && (
          <Texto category='label' appearance='hint' style={{ fontStyle: 'italic' }}>
            {subtitle}
          </Texto>
        )}
        <Divider />
        {inputHeader}
        <Select
          ref={ref}
          showSearch
          {...rest}
          value={savedValue?.value ?? value ?? ''}
          onChange={(_, opt) => setSavedValue(opt)}
        />
        {savedValue && (
          <Horizontal justifyContent='flex-end' style={{ marginTop: '1rem' }} gap='0.5rem'>
            <GraviButton buttonText='Cancel' onClick={() => setSavedValue(undefined)} />
            <GraviButton buttonText='Save' color='primary' onClick={onSave} />
          </Horizontal>
        )}
      </Vertical>
    )
  }
)
